import React, { useEffect, useMemo, useRef, useState } from 'react';
import StepRow from '../../playbooks/components/step-row';
import { describeBranch } from '../../playbooks/model/playbook-model';
import SectionHeading from '../section-heading';
import { MdMenuBook } from 'react-icons/md';
import { runtimeStepMap, slotValues, displayValue } from './debug-model';

export default function PlaybookDebugger({ config, session }) {
    const playbook = config.stm.metadata.playbook;
    const mapping = useMemo(() => runtimeStepMap(config), [config]);
    const current = mapping[session?.runningState] || mapping[session?.runningBlock];
    const activeRef = useRef(null);
    const values = slotValues(session, config.journeyId);
    const previous = useRef(null);
    const [changes, setChanges] = useState({});
    const [visited, setVisited] = useState(new Set());
    useEffect(() => {
        if (current) {
            setVisited(old => new Set([...old, current]));
            activeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        }
    }, [current]);
    useEffect(() => {
        const changed = {};
        if (previous.current) {
            for (const name of new Set([...Object.keys(previous.current), ...Object.keys(values)])) {
                if (JSON.stringify(previous.current[name]) !== JSON.stringify(values[name])) changed[name] = { before: previous.current[name] };
            }
        }
        setChanges(changed);
        previous.current = JSON.parse(JSON.stringify(values));
    }, [values]);
    const variables = [...new Set([...Object.keys(config.slots || {}), ...Object.keys(values), ...Object.keys(changes)])];
    const rows = (steps, prefix) => steps.map((step, index) => {
        const number = `${prefix}.${index + 1}`;
        const active = current === step.id;
        return <div key={step.id}>
            <div ref={active ? activeRef : null} aria-current={active ? 'step' : undefined} data-testid={`debug-step-${step.id}`}
                className={`my-1 rounded-lg border ${active ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-100 dark:border-blue-700 dark:bg-blue-900/20 dark:ring-blue-900/40' : 'border-transparent'}`}>
                <StepRow readOnly step={step} number={number} lookups={{ sections: playbook.sections }} />
                {(active || visited.has(step.id)) && <p className={`px-3 pb-2 text-xs font-medium ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-400'}`}>{active ? (session?.slotToFill ? 'Waiting for input' : 'Running') : 'Visited'}</p>}
            </div>
            {(step.branches || []).map((branch, branchIndex) => <div key={branch.id} className="ml-12 border-l-2 border-purple-100 pl-3 dark:border-purple-900">
                <p className="py-2 text-xs font-medium text-purple-700 dark:text-purple-300">{describeBranch(branch) || 'Condition'}</p>
                {rows(branch.steps || [], `${number}.${branchIndex + 1}`)}
            </div>)}
        </div>;
    });
    return <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <SectionHeading title={config.name || "Playbook"} subtitle="Playbook · Execution steps" icon={MdMenuBook} />
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/60 px-4 py-5">
            {!current && session?.runningState && <p className="mb-3 text-xs text-gray-500">Waiting for a matching playbook step. The running version may differ from this definition.</p>}
            {playbook.sections.map((section, index) => <section key={section.id} className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 border-b border-gray-100 px-1 pb-3 text-sm font-semibold text-gray-900"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs text-gray-500">{index + 1}</span>{section.title}</h3>
                {rows(section.steps || [], String(index + 1))}
            </section>)}
            {!playbook.sections.length && <p className="text-sm text-gray-500">This playbook has no steps.</p>}
        </div>
        <section aria-label="Playbook variables" className="max-h-64 shrink-0 overflow-y-auto border-t border-gray-200 bg-white px-4 py-4">
            <div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-semibold text-gray-900">Variables</h3><span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs tabular-nums text-gray-500">{variables.length}</span></div>
            {!variables.length && <p className="text-xs text-gray-500">No variables yet.</p>}
            {variables.map(name => <div key={name} className={`mb-2 rounded-lg border px-3 py-2 ${changes[name] ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20' : 'border-gray-200 bg-gray-50/60'}`}>
                <div className="flex items-center gap-2"><span className="break-all font-mono text-xs font-medium text-emerald-700 dark:text-emerald-400">{name}</span>{changes[name] && <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Changed</span>}</div>
                {changes[name] && <div className="mt-1 whitespace-pre-wrap break-all text-xs text-gray-400">Previous: {displayValue(changes[name].before)}</div>}
                <div className="mt-1 whitespace-pre-wrap break-all font-mono text-xs leading-relaxed text-gray-800">{displayValue(values[name])}</div>
            </div>)}
        </section>
    </div>;
}
